/**
 * 🛠 Phase 13.2 — Promo Operations panel
 *
 * Embedded inside the Promotion Control Center. Provides safe,
 * confirmation-gated admin operations that previously required curl +
 * a Bearer token. Two actions:
 *
 *   1. "Run Phase 11 Promo Backfill" — idempotent. Walks every
 *      currently-featured + spotlight submission and applies the
 *      matching tier. Skips tracks that already received their grant.
 *
 *   2. "Verify Featured Tracks" — read-only audit. Calls the public
 *      /api/discover/home + decorated /api/track/{id} so the operator
 *      can confirm promo grants landed without leaving the page.
 *
 * Both actions require an explicit confirmation modal before firing.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const PromoOperationsPanel = () => {
  const [confirmingBackfill, setConfirmingBackfill] = useState(false);
  const [running, setRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const runBackfill = async () => {
    setConfirmingBackfill(false);
    setRunning(true);
    setBackfillResult(null);
    try {
      const r = await api.post(
        "/admin/promo-plays/backfill-once",
        null,
        { headers: { "X-Confirm-Backfill": "yes" } },
      );
      setBackfillResult({ ok: true, payload: r.data });
      toast.success("Promo backfill completed");
      // Auto-trigger verification so the operator sees the live state.
      verifyFeatured();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Backfill failed";
      setBackfillResult({ ok: false, error: detail });
      toast.error(`Backfill failed: ${detail}`);
    } finally {
      setRunning(false);
    }
  };

  const verifyFeatured = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const home = await api.get("/discover/home");
      const featuredPush = home.data?.featured_push_today || [];
      const subs = featuredPush
        .filter((t) => ["featured", "spotlight"].includes((t.status || "").toLowerCase()))
        .map((t) => ({ id: t.submission_id, status: t.status, title: t.track_title }));

      // Re-probe each via the public decorated endpoint.
      const probes = await Promise.all(
        subs.map(async (s) => {
          try {
            const r = await api.get(`/track/${s.id}`);
            const sub = r.data?.submission || {};
            return {
              ...s,
              play_count: Number(sub.play_count || 0),
              promotional_plays: Number(sub.promotional_plays || 0),
              display_plays: Number(sub.display_plays || 0),
              tiers: sub.promotional_tiers_applied || [],
              momentum_badge: sub.momentum_badge || "",
            };
          } catch (_e) {
            return { ...s, error: true };
          }
        }),
      );

      const failing = probes.filter(
        (p) => !p.error && (p.promotional_plays === 0 || !p.tiers.length),
      );
      setVerifyResult({
        total: probes.length,
        ok: probes.length - failing.length,
        failing: failing.length,
        probes,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Verify failed";
      setVerifyResult({ error: detail });
      toast.error(`Verify failed: ${detail}`);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4"
      data-testid="promo-operations-panel"
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-lg">🛠</span>
        <h4 className="text-white font-bold">Promo Operations</h4>
      </div>
      <p className="text-xs text-gray-400 mb-3 max-w-3xl">
        Safe, audited admin operations. Each is idempotent and read-write-safe.
        Replaces the curl-with-Bearer-token workflow for the same backend endpoints.
      </p>

      {/* Actions row */}
      <div className="flex flex-wrap gap-3 mb-3">
        <Button
          onClick={() => setConfirmingBackfill(true)}
          disabled={running}
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          data-testid="promo-backfill-trigger"
        >
          {running ? "Running backfill…" : "🚀 Run Phase 11 Promo Backfill"}
        </Button>
        <Button
          onClick={verifyFeatured}
          disabled={verifying}
          variant="outline"
          className="text-white border-amber-500/40"
          data-testid="promo-verify-trigger"
        >
          {verifying ? "Verifying…" : "🔍 Verify Featured Tracks"}
        </Button>
      </div>

      {/* Backfill JSON summary */}
      {backfillResult && (
        <div className="mb-3" data-testid="promo-backfill-result">
          <div className="text-xs text-gray-400 mb-1">Backfill response</div>
          <pre
            className={`text-xs rounded-lg p-3 overflow-x-auto border max-h-72 ${
              backfillResult.ok
                ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-100"
                : "bg-red-950/30 border-red-500/30 text-red-100"
            }`}
          >
{backfillResult.ok
  ? JSON.stringify(backfillResult.payload, null, 2)
  : `ERROR: ${backfillResult.error}`}
          </pre>
        </div>
      )}

      {/* Live verification table */}
      {verifyResult && !verifyResult.error && (
        <div data-testid="promo-verify-result">
          <div className="flex items-center gap-3 text-sm mb-2">
            <span className="text-gray-400">Live verification:</span>
            <span className="text-emerald-300 font-semibold">
              ✓ {verifyResult.ok} OK
            </span>
            <span className={verifyResult.failing ? "text-red-300 font-semibold" : "text-gray-500"}>
              ✗ {verifyResult.failing} missing grant
            </span>
            <span className="text-gray-500">of {verifyResult.total} featured/spotlight</span>
          </div>
          <div className="rounded-lg border border-amber-500/15 bg-black/30 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="text-gray-400 border-b border-amber-500/15">
                <tr>
                  <th className="px-3 py-2">submission_id</th>
                  <th className="px-3 py-2">status</th>
                  <th className="px-3 py-2 text-right">play_count</th>
                  <th className="px-3 py-2 text-right">promo</th>
                  <th className="px-3 py-2 text-right">display</th>
                  <th className="px-3 py-2">tier(s)</th>
                  <th className="px-3 py-2">momentum</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                {verifyResult.probes.map((p) => {
                  const ok = !p.error && p.promotional_plays > 0;
                  return (
                    <tr key={p.id} className="border-b border-amber-500/10 last:border-0">
                      <td className="px-3 py-1.5 font-mono">{p.id}</td>
                      <td className="px-3 py-1.5">{p.status}</td>
                      <td className="px-3 py-1.5 text-right">{p.play_count}</td>
                      <td
                        className={`px-3 py-1.5 text-right font-semibold ${ok ? "text-emerald-300" : "text-red-300"}`}
                      >
                        {p.promotional_plays}
                      </td>
                      <td className="px-3 py-1.5 text-right">{p.display_plays}</td>
                      <td className="px-3 py-1.5">
                        {(p.tiers || []).length ? p.tiers.join(", ") : "—"}
                      </td>
                      <td className="px-3 py-1.5">{p.momentum_badge || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {verifyResult?.error && (
        <div className="text-sm text-red-300">{verifyResult.error}</div>
      )}

      {/* Confirmation modal */}
      <AlertDialog open={confirmingBackfill} onOpenChange={setConfirmingBackfill}>
        <AlertDialogContent
          className="bg-black border border-amber-500/40"
          data-testid="promo-backfill-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              🚀 Run Phase 11 Promo Backfill?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300 space-y-2">
              <span className="block">
                Walks every currently-featured + spotlight submission and
                applies the matching promotional-plays tier.
              </span>
              <span className="block">
                • <b>Standard</b> tier (+500) for featured tracks<br />
                • <b>Spotlight</b> tier (+1200) for spotlight pos 2-3<br />
                • <b>Mega</b> tier (+4000) for spotlight pos 1
              </span>
              <span className="block text-amber-200">
                Tracks that already received their tier are skipped (idempotent).
                Lifetime cap per track is enforced. Every grant is logged in
                <code className="mx-1 px-1 bg-black/40 rounded">promotional_plays_log</code>
                with your user_id as the actor.
              </span>
              <span className="block text-xs text-gray-500">
                Real <code>play_count</code> is never mutated. Analytics integrity preserved.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promo-backfill-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runBackfill}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="promo-backfill-confirm"
            >
              Run backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromoOperationsPanel;
